import { FilterService } from './../../utils/services/FilterService';
import { Formats, ICalendarForFilter, IFilters, IMeeting, ISearchAreaOption } from '@utils';
import { CalendarViewModel, ProfileViewModel } from '@views';
import { isMobile } from 'react-device-detect';
import { addDays, setHours } from 'date-fns';
import { action, computed, makeObservable, observable, reaction } from 'mobx';
import { Inject, Service } from 'typedi';
import { CalendarColorsStore, MembersStore, PrivateCalendarsStore } from '../../store';
import { RoomsStore } from './../../store/rooms';
@Service()
export class FilterViewModel {

  @Inject(() => FilterService)
  private filterService: FilterService;

  @Inject(() => PrivateCalendarsStore)
  private privateCalendarStore: PrivateCalendarsStore;

  @Inject(() => CalendarViewModel)
  private calendarViewModel: CalendarViewModel;

  @Inject(() => MembersStore)
  private membersStore: MembersStore;

  @Inject(() => RoomsStore)
  private roomsStore: RoomsStore;

  @Inject(() => CalendarColorsStore)
  private calendarColorsStore: CalendarColorsStore;

  @Inject(() => ProfileViewModel)
  private profileViewModel: ProfileViewModel;

  @observable.shallow privateCalendars: ICalendarForFilter[] = [];

  @computed get notCheckedCalendarsIds() {
    return this.filterService.notCheckedCalendarsIds;
  };

  @observable openPanel = false;

  @computed get selectEmployeeId() {
    return this.filterService.selectEmployeeId;
  };

  @computed get selectRoomCabinetNumber() {
    return this.filterService.selectRoomCabinetNumber;
  };

  @computed get showAllRoomsMeetings() {
    return this.filterService.showAllRoomsMeetings;
  };

  replacer = (key: keyof IFilters, value: IFilters[keyof IFilters]) => {
    if ((key === 'startInterval' || key === 'endInterval') && value) {
      const date = new Date(value as string);

      return setHours(date, key === 'startInterval' ? 0 : 23).toString();
    };
    return value;
  };

  @computed get filters() {
    return this.filterService.filters;
  }

  @computed get isAnyFilterHas() {
    return !!this.filterService.filters.who.length
      || this.filterService.filters.where
      || this.filterService.filters.description
      || this.filterService.filters.searchArea[0]?.mode !== 'ACTIVE'
      || this.filterService.filters.search
      || this.filterService.filters.format
      || this.filterService.filters.startInterval
      || this.filterService.filters.endInterval;
  }

  @computed get isFilterOnCurrentMonth() {
    const start = this.filterService.filters.startInterval ? new Date(this.filterService.filters.startInterval) : null;
    const end = this.filterService.filters.endInterval ? new Date(this.filterService.filters.endInterval) : null;
    if (start) {
      start.setHours(0);
      start.setMinutes(0);
      start.setSeconds(0);
    }
    if (end) {
      end.setHours(0);
      end.setMinutes(0);
      end.setSeconds(0);
    }
    return (!start || start >= this.calendarViewModel.viewCalendar[0])
      && (!end || end <= this.calendarViewModel.viewCalendar[this.calendarViewModel.viewCalendar.length - 1]);
  }

  @computed get searchAreaOptions(): ISearchAreaOption[] {
    return this.filterService.searchAreaOptions;
  }

  reactionsSubscribe = () => {
    reaction(
      () => this.privateCalendarStore.data,
      (calendars) => {
        const notCheckedCalendars = (JSON.parse(localStorage.getItem('notCheckedCalendars')) as number[]) ?? [];
        this.privateCalendars = [this.privateCalendarStore.innerCalendar, ...calendars].map(
          calendar => (
            { ...calendar, isSelected: !notCheckedCalendars.includes(calendar.id) }
          )
        );
      }
    );
  }

  constructor() {
    makeObservable(this); 
  };

  /**
 * @param property - поле объекта встречи, которое надо обновить
 * @param value - значение поля
 */
  @action onUpdateFilters = (property: keyof IFilters, value: IFilters[keyof IFilters]) => {
    property === 'startInterval'
      && this.filterService.filters.endInterval
      && ((value as Date) > this.filterService.filters.endInterval)
      && (this.filterService.filters.endInterval = addDays(value as Date, 1));

    property === 'format' && value === Formats.ONLINE && (this.filterService.filters.where = null);

    this.filterService.filters = { ...this.filterService.filters, [property]: value };

    sessionStorage.setItem('filters', JSON.stringify(this.filterService.filters, this.replacer));
    this.getFilteredCalendar();
  };

  @action onInit = () => {
    this.reactionsSubscribe();
    !this.membersStore.data.length && this.membersStore.getMembers();
    this.roomsStore.getRooms();

    if (this.privateCalendarStore.data.length)
      return this.privateCalendarStore.getConnectingUrls();
  };

  @action setSelectRoom = (cabinetNumber: number) => this.filterService.setSelectRoomResetEmployee(cabinetNumber);

  @action setSelectEmployee = (id?: number) => this.filterService.setSelectEmployeeResetRoom(id);

  @action setShowAllRoomsMeetings = (value: boolean) => this.filterService.setShowAllRoomsMeetings(value);

  @action getFilteredCalendar = () => !isMobile && this.calendarViewModel.getAllMeetings();

  @action setCheckedCalendar = (calendar: ICalendarForFilter) => {
    this.privateCalendars = this.privateCalendars.map(item => {

      if (item.id === calendar.id) {
        const notCheckedCalendars = (JSON.parse(localStorage.getItem('notCheckedCalendars')) as number[]) ?? [];

        const updatedCalendars = item.isSelected
          ? [...notCheckedCalendars, item.id]
          : notCheckedCalendars.filter(notCheckedCalendar => notCheckedCalendar !== item.id);

        localStorage.setItem('notCheckedCalendars', JSON.stringify(updatedCalendars));
        this.filterService.notCheckedCalendarsIds = updatedCalendars;

        return { ...item, isSelected: !item.isSelected };
      }
      return item;
    });
  };

  @action setOpenPanel = (newValue: boolean) => {
    this.openPanel = newValue;
  };

  @action resetEmployeeAndRoom = () =>
    this.filterService.setSelectEmployeeResetRoom();

  get isCalendarsLoading() {
    return this.privateCalendarStore.isLoading || this.calendarColorsStore.isLoading;
  }

  get isMembersLoading() {
    return this.membersStore.isLoading;
  }

  get roomsLoading() {
    return this.roomsStore.isLoading;
  }

  @computed get rooms() {
    return this.roomsStore.data;
  }

  setMonthCalendar = () => {
    this.calendarViewModel.setSelectedFilter('month');
    this.calendarViewModel.setFirstDayOfIntervalAsCurrent();
  };

  @computed get members() {
    return this.membersStore.data;
  }

  isValidMeeting = (meeting: IMeeting) => this.filterService.isValidMeeting(meeting, this.profileViewModel.minutesOffset);


  @action resetFilters = () => {
    this.filterService.resetFilters();
    this.getFilteredCalendar();
  };
}
